import type { Dispatch } from "@reduxjs/toolkit";
import type { Directory, File, Item, Pagination } from "@southfields-digital/mxmz-react-ui";
import { connect } from "react-redux";

import type { StateType } from "src/redux/reducers";
import {
	MEDIA_LIBRARY,
	type MediaLibraryStateType,
	REQUEST_COPY_FILES,
	REQUEST_CREATE_BULK_FILE_UPLOAD,
	REQUEST_CREATE_DIRECTORY,
	REQUEST_DELETE_DIRECTORY,
	REQUEST_DELETE_FILE,
	REQUEST_GET_COPY_DIALOG_DIRECTORIES,
	REQUEST_GET_DIRECTORIES,
	REQUEST_GET_FILE,
	REQUEST_GET_FILES,
	REQUEST_RENAME_FILE,
	REQUEST_SEARCH_DIRECTORIES,
	REQUEST_SEARCH_FILES,
	REQUEST_UPDATE_DIRECTORY,
} from "src/redux/reducers/mediaLibrary";
import { USER } from "src/redux/reducers/user";
import type { CopyFilesActionPayload } from "src/types/mediaLibrary";

export type MediaLibraryContainerProps = {
	count: number;
	copyFiles: (
		files: CopyFilesActionPayload["files"],
		destinationDirectoryId: CopyFilesActionPayload["destinationDirectoryId"],
		callback?: CopyFilesActionPayload["callback"],
	) => void;
	copyDialogDirectories: Directory[];
	createFolderAtPath: (...args: any) => void;
	deleteDirectory: (...args: any) => void;
	deleteFile: (...args: any) => void;
	directories: Directory[];
	getFileById: (id: string) => void;
	renameFile: (id: string, name: string) => void;
	fetchCopyDialogDirectories: (id: string) => void;
	fetchDirectoriesById: (id: string) => void;
	files: File[];
	getDataByDirectory: (...args: any) => void;
	isCopying: boolean;
	isCopyDialogDirectoriesLoading: boolean;
	isDirectoriesLoading: boolean;
	isLoading: boolean;
	isLocked?: boolean;
	searchCount: number;
	searchDataByDirectory: (...args: any) => void;
	searchResults: Item[];
	updateDirectoryName: ({
		id,
		name,
		onSuccess,
		onFailure,
	}: {
		id: string;
		name: string;
		onSuccess: () => void;
		onFailure: () => void;
	}) => void;
	uploadFiles: ({
		directoryId,
		fileList,
		callback,
	}: {
		directoryId: string;
		fileList: FileList;
		callback: (uploadedFileCount: number) => void;
	}) => void;
	workspaceId?: string;
};

export const mapStateToProps = (state: StateType) => {
	const { copyDialogDirectories, directories, files, isCopying }: MediaLibraryStateType =
		state[MEDIA_LIBRARY];
	const user = state[USER];

	return {
		copyDialogDirectories: (copyDialogDirectories.data?.directories ?? []) as Item[],
		count: files.data?.count ?? 0,
		directories: (directories.data?.directories ?? []) as Item[],
		files: (files.data?.files ?? []) as Item[],
		isCopying,
		isCopyDialogDirectoriesLoading: copyDialogDirectories.isLoading,
		isDirectoriesLoading: directories.isLoading,
		isLoading: directories.isLoading || files.isLoading,
		isLocked: directories?.data?.locked ?? false,
		searchCount: files.searchData?.count ?? 0,
		searchResults: [
			...(directories?.searchData?.directories ?? []),
			...(files.searchData?.files ?? []),
		],
		workspaceId: user?.auth?.workspace?.id,
	};
};

export const mapDispatchToProps = (dispatch: Dispatch) => ({
	copyFiles: (
		files: CopyFilesActionPayload["files"],
		destinationDirectoryId: CopyFilesActionPayload["destinationDirectoryId"],
		callback?: CopyFilesActionPayload["callback"],
	) =>
		dispatch({
			type: REQUEST_COPY_FILES,
			payload: {
				files,
				destinationDirectoryId,
				callback,
			},
		}),
	createFolderAtPath: (payload: {
		path: string;
		insertIntoDirectoryId: string;
		callback: () => void;
	}) => {
		dispatch({ type: REQUEST_CREATE_DIRECTORY, payload });
	},
	deleteDirectory: ({ id, callback }: { id: string; callback: () => void }) =>
		dispatch({
			type: REQUEST_DELETE_DIRECTORY,
			payload: {
				id,
				callback,
			},
		}),
	deleteFile: (id: string) => dispatch({ type: REQUEST_DELETE_FILE, payload: { id } }),
	getFileById: (id: string) => dispatch({ type: REQUEST_GET_FILE, payload: { id } }),
	renameFile: (id: string, name: string) =>
		dispatch({ type: REQUEST_RENAME_FILE, payload: { id, name } }),
	fetchCopyDialogDirectories: (id: string) =>
		dispatch({
			type: REQUEST_GET_COPY_DIALOG_DIRECTORIES,
			payload: {
				id,
			},
		}),
	fetchDirectoriesById: (id: string) =>
		dispatch({
			type: REQUEST_GET_DIRECTORIES,
			payload: {
				id,
			},
		}),
	getDataByDirectory: ({
		id,
		includeNested,
		initialPath,
		pagination,
		hideLoader,
	}: {
		id: string;
		includeNested?: boolean;
		initialPath?: Directory[];
		pagination?: Pagination;
		hideLoader?: boolean;
	}) => {
		dispatch({
			type: REQUEST_GET_DIRECTORIES,
			payload: {
				id,
				includeNested,
				initialPath,
				hideLoader,
			},
		});
		dispatch({
			type: REQUEST_GET_FILES,
			payload: {
				id,
				pagination,
				hideLoader,
			},
		});
	},
	searchDataByDirectory: ({
		id,
		pagination,
		searchTerm = "",
	}: {
		id: string;
		pagination?: Pagination;
		searchTerm?: string;
	}) => {
		const searchPayload = {
			includeNested: true,
			searchTerm: searchTerm,
		};

		dispatch({
			type: REQUEST_SEARCH_DIRECTORIES,
			payload: {
				id,
				...searchPayload,
			},
		});
		dispatch({
			type: REQUEST_SEARCH_FILES,
			payload: {
				id,
				pagination,
				...searchPayload,
			},
		});
	},
	updateDirectoryName: ({
		id,
		name,
		onSuccess,
		onFailure,
	}: {
		id: string;
		name: string;
		onSuccess: () => void;
		onFailure: () => void;
	}) => {
		dispatch({ type: REQUEST_UPDATE_DIRECTORY, payload: { name, id, onSuccess, onFailure } });
	},
	uploadFiles: ({
		directoryId,
		fileList,
		callback,
	}: {
		directoryId: string;
		fileList: FileList;
		callback: (uploadedFileCount: number) => void;
	}) =>
		dispatch({
			type: REQUEST_CREATE_BULK_FILE_UPLOAD,
			payload: {
				directoryId,
				fileList,
				callback,
			},
		}),
});

export const connectToMediaLibraryState = (WrappedComponent: React.FC<any>) =>
	connect(mapStateToProps, mapDispatchToProps)(WrappedComponent);
